import { Link, useSearchParams } from 'react-router-dom'
import { Activity } from 'lucide-react'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import { useIngestionDiagnosticsList } from '@/shared/hooks/api/ingestionDiagnostics'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { statusBadgeVariant } from '../utils/formatDiagnosticsTime'
import { IngestionErrorsPanel } from './IngestionErrorsPage'

type DiagnosticsTab = 'ingestions' | 'errors'

function parseDiagnosticsTab(value: string | null): DiagnosticsTab {
  if (value === 'errors') {
    return 'errors'
  }
  return 'ingestions'
}

export default function IngestionDiagnosticsListPage() {
  const { data: session, isFetched: isSessionFetched } = useGetUserSession()
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = parseDiagnosticsTab(searchParams.get('tab'))
  const searchQuery = searchParams.get('q') ?? ''
  const status = searchParams.get('status') ?? ''
  const origin =
    (searchParams.get('origin') as 'product' | 'packaging' | 'all' | null) ??
    'all'
  const attention =
    (searchParams.get('attention') as
      | 'stuck'
      | 'missingRagJobId'
      | 'pagesChunksMismatch'
      | null) ?? undefined

  const listQuery = useIngestionDiagnosticsList(
    {
      searchQuery: searchQuery || undefined,
      status: status || undefined,
      origin,
      attention,
    },
    {
      enabled:
        isSessionFetched &&
        Boolean(session?.isArtemAdmin) &&
        tab === 'ingestions',
    }
  )

  const items = listQuery.data?.pages.flatMap((page) => page.items) ?? []

  const setTab = (nextTab: DiagnosticsTab) => {
    const next = new URLSearchParams(searchParams)
    if (nextTab === 'ingestions') {
      next.delete('tab')
    } else {
      next.set('tab', nextTab)
    }
    setSearchParams(next)
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col overflow-auto p-4 sm:p-6'>
      <div className='mx-auto flex w-full max-w-7xl flex-col gap-6'>
        <div>
          <h1 className='text-2xl font-semibold tracking-tight'>
            Ingestion diagnostics
          </h1>
          <p className='text-sm text-muted-foreground'>
            Product and packaging document ingestions across all organizations
          </p>
        </div>

        <Tabs
          value={tab}
          onValueChange={(value) => {
            if (value === 'ingestions' || value === 'errors') {
              setTab(value)
            }
          }}
          className='gap-4'
        >
          <TabsList>
            <TabsTrigger value='ingestions'>Ingestions</TabsTrigger>
            <TabsTrigger value='errors'>Errors</TabsTrigger>
          </TabsList>

          <TabsContent value='ingestions' className='flex flex-col gap-6'>
            <div className='flex flex-wrap items-center gap-2'>
              <Input
                placeholder='Search source, product, packaging item, org, job id…'
                value={searchQuery}
                onChange={(event) => {
                  const next = new URLSearchParams(searchParams)
                  if (event.target.value) {
                    next.set('q', event.target.value)
                  } else {
                    next.delete('q')
                  }
                  setSearchParams(next)
                }}
                className='max-w-sm'
              />
              <Button
                variant={origin === 'all' ? 'default' : 'outline'}
                size='sm'
                onClick={() => {
                  const next = new URLSearchParams(searchParams)
                  next.set('origin', 'all')
                  setSearchParams(next)
                }}
              >
                All origins
              </Button>
              <Button
                variant={origin === 'product' ? 'default' : 'outline'}
                size='sm'
                onClick={() => {
                  const next = new URLSearchParams(searchParams)
                  next.set('origin', 'product')
                  setSearchParams(next)
                }}
              >
                Product
              </Button>
              <Button
                variant={origin === 'packaging' ? 'default' : 'outline'}
                size='sm'
                onClick={() => {
                  const next = new URLSearchParams(searchParams)
                  next.set('origin', 'packaging')
                  setSearchParams(next)
                }}
              >
                Packaging
              </Button>
              <Button
                variant={attention === 'stuck' ? 'default' : 'outline'}
                size='sm'
                onClick={() => {
                  const next = new URLSearchParams(searchParams)
                  if (attention === 'stuck') {
                    next.delete('attention')
                  } else {
                    next.set('attention', 'stuck')
                  }
                  setSearchParams(next)
                }}
              >
                Stuck
              </Button>
              <Button
                variant={
                  attention === 'missingRagJobId' ? 'default' : 'outline'
                }
                size='sm'
                onClick={() => {
                  const next = new URLSearchParams(searchParams)
                  if (attention === 'missingRagJobId') {
                    next.delete('attention')
                  } else {
                    next.set('attention', 'missingRagJobId')
                  }
                  setSearchParams(next)
                }}
              >
                No RAG job
              </Button>
              <Button
                variant={
                  attention === 'pagesChunksMismatch' ? 'default' : 'outline'
                }
                size='sm'
                onClick={() => {
                  const next = new URLSearchParams(searchParams)
                  if (attention === 'pagesChunksMismatch') {
                    next.delete('attention')
                  } else {
                    next.set('attention', 'pagesChunksMismatch')
                  }
                  setSearchParams(next)
                }}
              >
                Pages↔chunks gap
              </Button>
              <Button
                variant={status === 'FAILED' ? 'default' : 'outline'}
                size='sm'
                onClick={() => {
                  const next = new URLSearchParams(searchParams)
                  if (status === 'FAILED') {
                    next.delete('status')
                  } else {
                    next.set('status', 'FAILED')
                  }
                  setSearchParams(next)
                }}
              >
                Failed only
              </Button>
            </div>

            <div className='rounded-lg border bg-card'>
              <Table className='table-fixed'>
                <TableHeader>
                  <TableRow>
                    <TableHead className='w-[12rem]'>Source</TableHead>
                    <TableHead className='w-[9rem]'>Started</TableHead>
                    <TableHead className='w-[6rem]'>Origin</TableHead>
                    <TableHead className='w-[10rem]'>Organization</TableHead>
                    <TableHead className='w-[10rem]'>Parent</TableHead>
                    <TableHead className='w-[7rem]'>Upload</TableHead>
                    <TableHead className='w-[7rem]'>Lex status</TableHead>
                    <TableHead className='w-[7rem]'>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className='max-w-0 truncate'>
                        <Link
                          className='text-foreground underline decoration-dotted underline-offset-2'
                          to={`/admin/diagnostics/ingestions/${item.id}`}
                          title={item.name}
                        >
                          {item.name}
                        </Link>
                      </TableCell>
                      <TableCell className='whitespace-nowrap text-xs text-muted-foreground'>
                        {item.ingestedStartedAt
                          ? new Date(item.ingestedStartedAt).toLocaleString()
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant='outline'>{item.origin}</Badge>
                      </TableCell>
                      <TableCell
                        className='max-w-0 truncate'
                        title={item.organization.name}
                      >
                        {item.organization.name}
                      </TableCell>
                      <TableCell className='max-w-0 truncate'>
                        {item.origin === 'packaging' && item.packagingItem ? (
                          <span title={item.packagingItem.name}>
                            {item.packagingItem.name}
                          </span>
                        ) : (
                          <span title={item.product?.name ?? item.product?.id}>
                            {item.product?.name ?? item.product?.id ?? '—'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className='text-xs text-muted-foreground'>
                        {item.origin === 'product' ? (
                          <>
                            {item.fileUploadStatus ?? '—'}
                            {item.filePathInBucket != null &&
                              item.filePathInBucket.trim().length === 0 &&
                              (item.fileUploadStatus === 'PENDING' ||
                                item.fileUploadStatus === 'FAILED') && (
                                <span className='mt-0.5 block text-amber-600'>
                                  No OSS path
                                </span>
                              )}
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={statusBadgeVariant(
                            item.ingestionStatus ?? ''
                          )}
                        >
                          {item.ingestionStatus ?? '—'}
                        </Badge>
                      </TableCell>
                      <TableCell
                        className='max-w-0 truncate font-mono text-xs'
                        title={item.ingestionErrorCode ?? undefined}
                      >
                        {item.ingestionErrorCode ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {items.length === 0 && !listQuery.isLoading && (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className='py-8 text-center text-muted-foreground'
                      >
                        No ingestions match the current filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {listQuery.hasNextPage && (
              <Button
                variant='outline'
                onClick={() => listQuery.fetchNextPage()}
                disabled={listQuery.isFetchingNextPage}
              >
                Load more
              </Button>
            )}

            {listQuery.isLoading && (
              <p className='flex items-center gap-2 text-sm text-muted-foreground'>
                <Activity className='size-4 animate-spin' />
                Loading ingestions…
              </p>
            )}
          </TabsContent>

          <TabsContent value='errors'>
            <IngestionErrorsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
